import { useEffect, useMemo, useState } from 'react';
import { usePaginationAndSort } from '@restore/helpers';
import { debounce } from 'lodash';
import { useParams } from 'react-router-dom';
import { useSupportSubjectsListSlice } from '@restore/SupportSubject/SupportSubject.slice';

export const useSupportSubjectListFilters = () => {
  const [patientCode, setPatientCode] = useState<string>('');

  const { id: clinicalTrialId } = useParams<{ id: string }>();

  const [search, fetchSupportSubjectsList, setSearch] =
    useSupportSubjectsListSlice((state, actions) => [
      state.search,
      actions.fetchSupportSubjectsList,
      actions.setSearchValue,
    ]);

  const { setSortBy, sortBy, currentPage, currentPageSize, setCurrentPage } =
    usePaginationAndSort('subjectList');

  const onSort = (name: string, dir?: string) => {
    const sort = dir ? `${name},${dir}` : undefined;
    setSortBy({ listName: 'subjectList', newSortBy: sort });
  };

  const onSearch = useMemo(
    () =>
      debounce((value: string) => {
        const ok = (value && value.length > 1) || value === '';
        if (!ok) return;

        setCurrentPage({ listName: 'subjectList', newPage: 1 });
        setSearch(value);
      }, 500),
    [setCurrentPage]
  );

  useEffect(() => onSearch.cancel(), [onSearch]);

  useEffect(() => {
    if (!clinicalTrialId) return;

    fetchSupportSubjectsList({
      clinicalTrialId,
      args: {
        patientCode: search,
        sort: sortBy,
        page: currentPage,
        size: currentPageSize,
      },
    });
  }, [clinicalTrialId, search, sortBy, currentPage, currentPageSize]);

  return {
    patientCode,
    setPatientCode,
    onSort,
    onSearch,
    setSortBy,
    sortBy,
    currentPage,
    currentPageSize,
    setCurrentPage,
  };
};
